import { getMetricDisplayName, getMetricShortName } from '@src/utils/metrics';
import { CURRENT_PRODUCT_MODE } from '@utils/constants';
import { get } from 'lodash';
export interface MetricDefinition {
  abbr: string;
  name: string;
  cm_metric: string;
  cm_value: number;
  cm_label: string;
  direction: '<' | '>';
  units: string;
  type: string;
  idx: number;
}

export interface MetricInfo {
  [abbr: string]: MetricDefinition;
}

// copied from pulse4/alerts3/metrics.py
export const getMetricInfo = () => ({
  vsf: {
    abbr: getMetricShortName({ metricKey: 'VideoStartFailures' }),
    name: getMetricDisplayName({ metricKey: 'VideoStartFailures' }),
    cm_metric: 'attempts',
    cm_value: 1000,
    cm_label: 'Attempts',
    direction: '>',
    units: '%',
    type: '',
    idx: 0,
  },
  ebvs: {
    abbr: getMetricShortName({ metricKey: 'ExitBeforeVideoStarts' }),
    name: getMetricDisplayName({ metricKey: 'ExitBeforeVideoStarts' }),
    cm_metric: 'attempts',
    cm_value: 1000,
    cm_label: 'Attempts',
    direction: '>',
    units: '%',
    type: '',
    idx: 1,
  },
  rr: {
    abbr: 'Rebuff %',
    name: getMetricDisplayName({ metricKey: 'RebufferingRatio' }),
    cm_metric: 'pcv',
    cm_value: 500,
    cm_label: 'Plays',
    direction: '>',
    units: '%',
    type: '',
    idx: 2,
  },
  pcv: {
    abbr: 'Plays',
    name: 'Concurrent Plays',
    cm_metric: 'min',
    cm_value: 5,
    cm_label: 'Minutes',
    direction: '<',
    units: 'plays',
    type: '',
    idx: 3,
  },
  abr: {
    abbr: getMetricShortName({ metricKey: 'Bitrate' }),
    name: getMetricDisplayName({ metricKey: 'Bitrate' }),
    cm_metric: 'pcv',
    cm_value: 500,
    cm_label: 'Plays',
    direction: '<',
    units: 'Mbps',
    type: '',
    idx: 4,
  },
  ast: {
    abbr: getMetricShortName({ metricKey: 'VideoStartTime' }),
    name: getMetricDisplayName({ metricKey: 'VideoStartTime' }),
    cm_metric: 'attempts',
    cm_value: 1000,
    cm_label: 'Attempts',
    direction: '>',
    units: 'sec',
    type: '',
    idx: 5,
  },
  attempts: {
    abbr: 'Attempts',
    name: 'Total Attempts',
    cm_metric: 'min',
    cm_value: 5,
    cm_label: 'Minutes',
    direction: '<',
    units: 'attempts',
    type: '',
    idx: 6,
  },
  pods_started: {
    abbr: 'Started',
    name: 'Ad Breaks Started',
    cm_metric: 'min',
    cm_value: 5,
    cm_label: 'Minutes',
    direction: '<',
    units: 'Ad Breaks',
    type: 'pods',
    idx: 0,
  },
  pods_completed: {
    abbr: 'Completed',
    name: 'Ad Breaks Completed',
    cm_metric: 'pods_ended',
    cm_value: 500,
    cm_label: 'Ad Breaks Ended',
    direction: '<',
    units: 'Ad Breaks',
    type: 'pods',
    idx: 1,
  },
  pods_abandoned: {
    abbr: 'Abandoned',
    name: 'Ad Breaks Abandoned',
    cm_metric: 'pods_ended',
    cm_value: 500,
    cm_label: 'Ad Breaks Ended',
    direction: '>',
    units: '%',
    type: 'pods',
    idx: 2,
  },
  pods_duration: {
    abbr: 'Duration',
    name: 'Ad Breaks Actual Duration',
    cm_metric: 'pods_completed',
    cm_value: 500,
    cm_label: 'Ad Breaks Completed',
    direction: '<',
    units: 'sec',
    type: 'pods',
    idx: 3,
  },
  pods_ended_play: {
    abbr: 'Ended Play',
    name: 'Ad Breaks / Ended Attempts',
    cm_metric: 'pods_completed',
    cm_value: 500,
    cm_label: 'Ad Breaks Completed',
    direction: '>',
    units: 'Ad Breaks',
    type: 'pods',
    idx: 4,
  },
  pods_ended: {
    abbr: 'Ended',
    name: 'Ad Breaks Ended',
    cm_metric: 'min',
    cm_value: 5,
    cm_label: 'Minutes',
    direction: '<',
    units: 'Ad Breaks',
    type: 'pods',
    idx: 5,
  },
  ads_concurrent_plays: {
    abbr: 'Plays',
    name: 'Ad Concurrent Plays',
    cm_metric: 'min',
    cm_value: 500,
    cm_label: 'Minutes',
    direction: '<',
    units: '',
    type: 'ads',
    idx: 8,
  },
  ads_plays: {
    abbr: 'Plays',
    name: 'Ad Impressions',
    cm_metric: 'min',
    cm_value: 500,
    cm_label: 'Minutes',
    direction: '<',
    units: '',
    type: 'ads',
    idx: 9,
  },
  ads_rebuffering_ratio: {
    abbr: 'Rebuff %',
    name: 'Ad Rebuffering Ratio',
    cm_metric: 'ads_plays',
    cm_value: 500,
    cm_label: 'Ad Impressions',
    direction: '>',
    units: '%',
    type: 'ads',
    idx: 10,
  },
  ads_average_bitrate: {
    abbr: 'Avg Bitrate',
    name: 'Ad Average Bitrate',
    cm_metric: 'ads_plays',
    cm_value: 500,
    cm_label: 'Ad Impressions',
    direction: '<',
    units: 'Mbps',
    type: 'ads',
    idx: 11,
  },
  ads_video_startup_time: {
    abbr: 'VST',
    name: 'Ad Startup Time',
    cm_metric: 'ads_plays',
    cm_value: 500,
    cm_label: 'Ad Impressions',
    direction: '>',
    units: 'sec',
    type: 'ads',
    idx: 12,
  },
  vmf: {
    abbr: getMetricShortName({ metricKey: 'VideoPlaybackFailures' }),
    name: getMetricDisplayName({ metricKey: 'VideoPlaybackFailures' }),
    cm_metric: 'ended plays',
    cm_value: 1000,
    cm_label: 'Ended Plays',
    direction: '>',
    units: '%',
    type: '',
    idx: 13,
  },
  vrt: {
    abbr: getMetricShortName({ metricKey: 'VideoRestartTime' }),
    name: getMetricDisplayName({ metricKey: 'VideoRestartTime' }),
    cm_metric: 'ended plays',
    cm_value: 1000,
    cm_label: 'Ended Plays',
    direction: '>',
    units: 'sec',
    type: '',
    idx: 14,
  },
  nir: {
    abbr: 'CIRR',
    name: 'Connection Induced Rebuffering Ratio',
    cm_metric: 'pcv',
    cm_value: 1000,
    cm_label: 'Plays',
    direction: '>',
    units: '%',
    type: '',
    idx: 15,
  },
  ad_attempts: {
    abbr: 'Attempts',
    name: 'Ad Attempts',
    cm_metric: 'min',
    cm_value: 5,
    cm_label: 'Minutes',
    direction: '<',
    units: 'attempts',
    type: 'ads',
    idx: 16,
  },
  ad_start_failures: {
    abbr: 'ASF',
    name: 'Ad Start Failures',
    cm_metric: 'attempts',
    cm_value: 1000,
    cm_label: 'Attempts',
    direction: '>',
    units: '%',
    type: 'ads',
    idx: 17,
  },
  exits_before_ad_start: {
    abbr: 'EBAS',
    name: 'Exits Before Ad Start',
    cm_metric: 'attempts',
    cm_value: 1000,
    cm_label: 'Attempts',
    direction: '>',
    units: '%',
    type: 'ads',
    idx: 18,
  },
  ad_playback_failures: {
    abbr: 'APF',
    name: 'Ad Playback Failures',
    cm_metric: 'ended plays',
    cm_value: 1000,
    cm_label: 'Ended Plays',
    direction: '>',
    units: '%',
    type: 'ads',
    idx: 19,
  },
  slates_duration: {
    abbr: 'ASD',
    name: '% Slate Duration',
    cm_metric: 'ss_ad_plays',
    cm_value: 500,
    cm_label: 'Server Side Ad Plays',
    direction: '>',
    units: '%',
    type: 'ads',
    idx: 20,
  },
  slates_started: {
    abbr: 'ASP',
    name: '% Slate Plays',
    cm_metric: 'ss_ad_plays',
    cm_value: 500,
    cm_label: 'Server Side Ad Plays',
    direction: '>',
    units: '%',
    type: 'ads',
    idx: 21,
  },
  vsf_technical: {
    abbr: getMetricShortName({ metricKey: 'VideoStartFailuresTech' }),
    name: getMetricDisplayName({ metricKey: 'VideoStartFailuresTech' }),
    cm_metric: 'attempts',
    cm_value: 1000,
    cm_label: 'Attempts',
    direction: '>',
    units: '%',
    type: '',
    idx: 22,
  },
  vpf_technical: {
    abbr: getMetricShortName({ metricKey: 'VideoPlaybackFailuresTech' }),
    name: getMetricDisplayName({ metricKey: 'VideoPlaybackFailuresTech' }),
    cm_metric: 'ended plays',
    cm_value: 1000,
    cm_label: 'Ended Plays',
    direction: '>',
    units: '%',
    type: '',
    idx: 23,
  },
  vsf_business: {
    abbr: getMetricShortName({ metricKey: 'VideoStartFailuresBusiness' }),
    name: getMetricDisplayName({ metricKey: 'VideoStartFailuresBusiness' }),
    cm_metric: 'attempts',
    cm_value: 1000,
    cm_label: 'Attempts',
    direction: '>',
    units: '%',
    type: '',
    idx: 24,
  },
  vpf_business: {
    abbr: getMetricShortName({ metricKey: 'VideoPlaybackFailuresBusiness' }),
    name: getMetricDisplayName({ metricKey: 'VideoPlaybackFailuresBusiness' }),
    cm_metric: 'ended plays',
    cm_value: 1000,
    cm_label: 'Ended Plays',
    direction: '>',
    units: '%',
    type: '',
    idx: 25,
  },
});

export const CM_UNIT = {
  'ended plays': {
    name: 'Ended Plays',
    units: 'Ended Plays',
    abbr: 'EP',
  },
  ss_ad_plays: {
    name: 'Server Side Ad Plays',
    units: 'Server Side Ad Plays',
    abbr: 'SSAP',
  },
};

const metricType = CURRENT_PRODUCT_MODE === 'EI' ? '' : 'ads';
export const PRODUCT_METRICS = Object.entries(getMetricInfo())
  .filter(([abbr, info]) => info.type === metricType)
  .reduce((acc, curr) => {
    acc[curr[0]] = curr[1];
    return acc;
  }, {});
